import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  DeepLinkIntent,
  DeepLinkDeploymentType,
  getDeepLinkIntent,
  clearDeepLinkIntent,
  resolveModelAlias,
} from '@/lib/deeplink';

export interface UseDeepLinkIntentResult {
  // The resolved intent (from URL params or sessionStorage)
  intent: DeepLinkIntent | null;
  // Specific fields for convenience
  deploymentType: DeepLinkDeploymentType | undefined;
  modelId: string | undefined;
  // Clear the intent (call after consuming)
  clearIntent: () => void;
  // Whether we have a valid intent
  hasIntent: boolean;
}

/**
 * Hook for consuming deep-link intents on target pages
 *
 * Priority:
 * 1. URL query params (deploymentType, model)
 * 2. sessionStorage intent
 *
 * Call clearIntent() after applying the intent to ensure one-time behavior.
 */
export const useDeepLinkIntent = (): UseDeepLinkIntentResult => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [intent, setIntent] = useState<DeepLinkIntent | null>(null);

  useEffect(() => {
    // Check URL params first
    const urlDeploymentType = searchParams.get('deploymentType') as DeepLinkDeploymentType | null;
    const urlModel = searchParams.get('model');

    if (urlDeploymentType || urlModel) {
      // Build intent from URL params
      const urlIntent: DeepLinkIntent = {
        action: 'gpu-instance',
        type: urlDeploymentType || undefined,
        model: urlModel ? resolveModelAlias(urlModel) : undefined,
        timestamp: Date.now(),
      };
      setIntent(urlIntent);

      // Clean URL params after capturing
      const newParams = new URLSearchParams(searchParams);
      newParams.delete('deploymentType');
      newParams.delete('model');
      setSearchParams(newParams, { replace: true });
      return;
    }

    // Fall back to sessionStorage intent
    const storedIntent = getDeepLinkIntent();
    if (storedIntent) {
      setIntent(storedIntent);
    }
  }, []);

  const clearIntent = useCallback(() => {
    setIntent(null);
    clearDeepLinkIntent();
  }, []);

  return {
    intent,
    deploymentType: intent?.type,
    modelId: intent?.model,
    clearIntent,
    hasIntent: intent !== null,
  };
};

export default useDeepLinkIntent;
